import { type RefObject, useContext, useRef } from 'react';

import { ganttChartTooltipCSS } from './GanttChartTooltip.sty.js';
import { GanttChartTooltipContent } from './GanttChartTooltipContent.js';
import { useChartActionsMenu } from '../../../core/components/series-action/hooks/useChartActionsMenu.js';
import { ChartTooltip } from '../../../core/components/tooltip/ChartTooltip/ChartTooltip.js';
import { Point } from '../../../core/types/point.js';
import { DEFAULT_TOOLTIP_MARGIN } from '../../constants/defaults.js';
import { GanttSegmentsContext } from '../../context/GanttSegmentsContext.js';
import { useRegisterTooltipHandlerCallbacks } from '../../hooks/use-register-tooltip-handler-callbacks.js';
import {
  useTooltipPinnedStatus,
  useTooltipPosition,
} from '../../hooks/use-set-tooltip-state.js';

/**
 * Tooltip specific to the Gantt Chart.
 */
export const GanttChartTooltip = () => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const pinned = useTooltipPinnedStatus();
  const mousePosition = useTooltipPosition();
  const { hovering = true } = useContext(GanttSegmentsContext);
  const {
    selectedItem: selectedItemId,
    actionsMenuRef,
    updateSelectedItem,
    onLeave,
  } = useChartActionsMenu();
  useRegisterTooltipHandlerCallbacks(tooltipRef);

  const isTooltipHeightKnown = useRef(false);

  if (!mousePosition || !hovering) {
    return null;
  }

  if (!isTooltipHeightKnown.current && tooltipRef.current) {
    isTooltipHeightKnown.current = true;
    return null;
  }

  const position = getTooltipPosition(mousePosition, tooltipRef);

  return (
    <ChartTooltip
      ref={tooltipRef}
      pinned={pinned}
      className={ganttChartTooltipCSS}
      position={position}
      onMouseLeave={onLeave}
      actionSelectionValue={{
        selectedItemId,
        actionsMenuRef,
        updateSelectedItem,
      }}
    >
      <GanttChartTooltipContent />
    </ChartTooltip>
  );
};

function getTooltipPosition(
  mousePosition: Point,
  tooltipRef: RefObject<HTMLDivElement | null>,
) {
  if (!tooltipRef.current) {
    // place the tooltip far off-screen on first render to measure its height
    return {
      x: -9999,
      y: -9999,
    };
  }

  const position: Point = {
    x: mousePosition.x,
    y: mousePosition.y + DEFAULT_TOOLTIP_MARGIN,
  };

  const tooltipHeight = tooltipRef.current.getBoundingClientRect().height;
  const viewportHeight = window.innerHeight;

  // check if tooltip is outside viewport
  if (position.y + tooltipHeight > viewportHeight) {
    // adjust vertical position so that tooltip is not cut off
    position.y = viewportHeight - tooltipHeight;
  }

  return {
    x: Math.max(0, position.x),
    y: Math.max(0, position.y),
  };
}
